// Forms · TimePickerField parsing helpers
//
// Split out of `TimePickerField.tsx` so that module exports components only.

import type { Dayjs } from "dayjs";

/** Pad a numeric string to at least 2 digits. */
function pad2(s: string): string {
  return s.length < 2 ? s.padStart(2, "0") : s;
}

/**
 * Parse a loose user-typed time string into a canonical "HH:mm" or
 * "HH:mm:ss" string, or return `null` if the input is empty / invalid.
 *
 * Accepted loose formats (all delimiters: ":", ".", " "):
 *   "14"        → "14:00[:00]"
 *   "9"         → "09:00[:00]"
 *   "1430"      → "14:30[:00]"
 *   "143000"    → "14:30:00"   (only when includeSeconds)
 *   "14:30"     → "14:30[:00]"
 *   "14.30"     → "14:30[:00]"
 *   "14 30"     → "14:30[:00]"
 *   "14:30:45"  → "14:30:45"   (only when includeSeconds)
 *   "25:00"     → null (hour out of range)
 *   ""          → null
 */
export function parseTimeInput(
  raw: string,
  includeSeconds: boolean,
): string | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;

  let h = "";
  let m = "";
  let s = "00";

  // ── Delimited: "14:30", "14.30", "14 30", "14:30:45" ──────────────────
  const delimited = trimmed.match(
    /^(\d{1,2})[:.\s](\d{1,2})(?:[:.\s](\d{1,2}))?$/,
  );
  if (delimited) {
    h = delimited[1];
    m = delimited[2];
    if (includeSeconds && delimited[3] != null) {
      s = delimited[3];
    }
  } else if (/^\d+$/.test(trimmed)) {
    // ── Pure digits: "14", "1430", "143000" ─────────────────────────────
    const digits = trimmed;
    if (digits.length <= 2) {
      h = digits;
      m = "00";
    } else if (digits.length === 3) {
      h = digits.slice(0, 1);
      m = digits.slice(1, 3);
    } else if (digits.length === 4) {
      h = digits.slice(0, 2);
      m = digits.slice(2, 4);
    } else if (digits.length === 6 && includeSeconds) {
      h = digits.slice(0, 2);
      m = digits.slice(2, 4);
      s = digits.slice(4, 6);
    } else {
      return null; // unrecognised digit string
    }
  } else {
    return null; // unrecognised format
  }

  const hNum = parseInt(h, 10);
  const mNum = parseInt(m, 10);
  const sNum = parseInt(s, 10);

  if (
    isNaN(hNum) || hNum < 0 || hNum > 23 ||
    isNaN(mNum) || mNum < 0 || mNum > 59 ||
    isNaN(sNum) || sNum < 0 || sNum > 59
  ) {
    return null;
  }

  const base = `${pad2(String(hNum))}:${pad2(String(mNum))}`;
  return includeSeconds ? `${base}:${pad2(String(sNum))}` : base;
}

/** Convert a `string | Dayjs | undefined` value prop into a display string.
 *  Strips trailing seconds when `includeSeconds` is false so Postgres `time`
 *  values (which round-trip as `HH:mm:ss`) render as `HH:mm`. */
export function toDisplayString(
  value: string | Dayjs | undefined,
  includeSeconds: boolean,
): string {
  if (value == null) return "";
  if (typeof value === "string") {
    if (!includeSeconds && /^\d{2}:\d{2}:\d{2}$/.test(value)) {
      return value.slice(0, 5);
    }
    return value;
  }
  // Dayjs instance
  return value.format(includeSeconds ? "HH:mm:ss" : "HH:mm");
}

